import React from "react";
import { Info, Loader, Printer } from "lucide-react";
import SearchableSelect from "../SearchableSelect";

export default function RequestTable({
  data,
  loading,
  error,
  currentPage,
  itemsPerPage,
  totalEntries,
  startEntry,
  endEntry,
  maxPages,
  onInfo,
  onPageChange,
  onItemsPerPageChange,
  onSort,
  sortBy,
  sortOrder,
}) {
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DRAFT":
        return "bg-gray-50 border border-gray-200 text-gray-700";
      case "SUBMITTED":
        return "bg-yellow-50 border border-yellow-200 text-yellow-700";
      case "APPROVED":
        return "bg-green-50 border border-green-200 text-green-700";
      case "ORDERED":
        return "bg-blue-50 border border-blue-200 text-blue-700";
      case "PARTIAL_RECEIVED":
        return "bg-orange-50 border border-orange-200 text-orange-700";
      case "COMPLETED":
        return "bg-teal-50 border border-teal-200 text-teal-700";
      case "CANCELLED":
        return "bg-red-50 border border-red-200 text-red-700";
      case "REJECTED":
        return "bg-red-50 border border-red-200 text-red-700";
      default:
        return "bg-gray-100 border border-gray-300 text-gray-700";
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case "DRAFT":
        return "Draft";
      case "SUBMITTED":
        return "Diajukan";
      case "APPROVED":
        return "Disetujui";
      case "ORDERED":
        return "Dipesan";
      case "PARTIAL_RECEIVED":
        return "Diterima Sebagian";
      case "COMPLETED":
        return "Selesai";
      case "CANCELLED":
        return "Dibatalkan";
      case "REJECTED":
        return "Ditolak";
      default:
        return status || "-";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "-";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return sortOrder === "asc" ? (
      <span className="text-teal-600 ml-1">↑</span>
    ) : (
      <span className="text-teal-600 ml-1">↓</span>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 mt-6">
      {/* Loading State */}
      {loading && (
        <div className="px-6 py-12 flex items-center justify-center text-gray-500">
          <Loader className="w-5 h-5 animate-spin mr-2" />
          Loading data...
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-700">
          Error: {error}
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                    Aksi
                  </th>
                  <th
                    onClick={() => onSort("status")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    Status <SortIcon column="status" />
                  </th>
                  <th
                    onClick={() => onSort("date")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    Tanggal PO <SortIcon column="date" />
                  </th>
                  <th
                    onClick={() => onSort("supplier")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    Nama Supplier <SortIcon column="supplier" />
                  </th>
                  <th
                    onClick={() => onSort("totalItems")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    Produk <SortIcon column="totalItems" />
                  </th>
                  <th
                    onClick={() => onSort("totalPrice")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    Perkiraan Belanja <SortIcon column="totalPrice" />
                  </th>
                  <th
                    onClick={() => onSort("createdBy")}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    Dibuat oleh <SortIcon column="createdBy" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-8 text-center text-gray-500">
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-200 hover:bg-gray-50 transition">
                      {/* Action Buttons */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() =>
                              window.open(
                                `/inventori/nota-po/${item.id}`,
                                "_blank",
                                "noopener,noreferrer",
                              )
                            }
                            className="w-8 h-8 bg-slate-500 text-white rounded-lg hover:bg-slate-600 flex items-center justify-center transition-colors"
                            title="Cetak Nota">
                            <Printer className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onInfo(item.id)}
                            className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                            title="Detail">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded border text-xs font-medium ${getStatusColor(
                            item.status,
                          )}`}>
                          {getStatusLabel(item.status)}
                        </span>
                      </td>

                      {/* Tanggal PO */}
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {formatDate(item.request_date)}
                      </td>

                      {/* Nama Supplier */}
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {item.supplier?.name || "-"}
                      </td>

                      {/* Produk */}
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {Array.isArray(item.items)
                          ? item.items.reduce(
                              (sum, i) => sum + (i.qty_ordered || 0),
                              0,
                            )
                          : (item.item_count ?? "-")}
                      </td>

                      {/* Perkiraan Belanja */}
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatCurrency(item.total_estimated_amount)}
                      </td>

                      {/* Dibuat oleh */}
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {item.created_by_name || item.created_by?.name || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {startEntry} to {endEntry} of {totalEntries} entries
              </span>
              <SearchableSelect
                options={[
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                ]}
                value={itemsPerPage}
                onChange={(value) => onItemsPerPageChange(value)}
                placeholder="Items per page"
                displayKey="label"
                valueKey="value"
                className="w-20"
                searchable={false}
                size="sm"
              />
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹‹
              </button>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ‹
              </button>

              {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
                let p = currentPage;
                if (currentPage === 1) p = 1;
                else if (currentPage === maxPages) p = maxPages - 2;
                else p = currentPage - 1;

                if (maxPages < 3) p = 1;

                const pageNum = p + i;
                if (pageNum > maxPages || pageNum < 1) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20"
                        : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"
                    }`}>
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  onPageChange(Math.min(maxPages, currentPage + 1))
                }
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ›
              </button>
              <button
                onClick={() => onPageChange(maxPages)}
                disabled={currentPage === maxPages}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition">
                ››
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
